/**
 * `<wyatt-analytics data-event="…">` — declarative pixel publisher.
 *
 * On connect, payload is `#parsePayload()` from a child `[data-payload]`
 * script, falling back to a pre-set `.payload` property (for JS-rendered
 * content such as rewards offers). Most events publish immediately via
 * `WYATT.Analytics.fromElement`. The `select_*` events wait for a bubbled
 * click on an `<a>` instead.
 *
 * - `select_item` builds its payload from the parent `view_item_list` + index
 * - `select_offer` and `select_nav_item` publish the element's own payload
 * - `select_banner` adds the clicked anchor, which a hero needs because its
 *   blocks are `@theme` / `@app` and Liquid cannot reach in to wrap a link
 * - `view_banner` inside a `[data-menu]` waits for the menu to open, once
 */
import { registerComponent } from "helpers";

registerComponent(
  "wyatt-analytics",
  class WyattAnalytics extends HTMLElement {
    /** @type {HTMLElement | null} */
    #dialog = null;

    /** @type {HTMLElement | null} */
    #menu = null;

    connectedCallback() {
      this.payload = this.#parsePayload() ?? this.payload;

      switch (this.dataset.event) {
        case "select_item":
          this.addEventListener("click", this.handleSelectItem.bind(this));
          return;
        case "select_offer":
        case "select_nav_item":
          this.addEventListener("click", this.handleSelectLink.bind(this));
          return;
        case "select_banner":
          this.addEventListener("click", this.handleSelectBanner.bind(this));
          return;
      }

      const dialog = this.closest("[data-dialog]");

      if (dialog && !this.#isDialogOpen(dialog)) {
        this.#deferUntilDialogOpen(dialog);
        return;
      }

      const menu = this.closest("[data-menu]");

      if (menu && !this.#isMenuOpen(menu)) {
        this.#deferUntilMenuOpen(menu);
        return;
      }

      this.#publish();
    }

    disconnectedCallback() {
      if (this.#dialog) {
        this.#dialog.removeEventListener("drawerOpen", this.#onDialogOpen);
        this.#dialog.removeEventListener("modalOpen", this.#onDialogOpen);
        this.#dialog = null;
      }

      if (this.#menu) {
        this.#menu.removeEventListener("menuOpen", this.#onMenuOpen);
        this.#menu = null;
      }
    }

    handleSelectItem(event) {
      if (!event.target.closest("a")) {
        return;
      }

      const list = this.closest('wyatt-analytics[data-event="view_item_list"]');
      const { index } = this.payload;
      const {
        item_list_id: itemListId,
        item_list_name: itemListName,
        resource,
      } = list.payload;

      WYATT.Analytics.publish(WYATT.Analytics.events.select_item, {
        item_list_id: itemListId,
        item_list_name: itemListName,
        index,
        product: resource.results[index],
      });
    }

    handleSelectLink(event) {
      if (!event.target.closest("a")) {
        return;
      }

      this.#publish();
    }

    handleSelectBanner(event) {
      const link = event.target.closest("a");

      if (!link) {
        return;
      }

      const linkText = link.textContent.trim();

      WYATT.Analytics.publish(WYATT.Analytics.events.select_banner, {
        ...this.payload,
        link_url: link.getAttribute("href"),
        link_text: linkText || null,
      });
    }

    #isDialogOpen(dialog) {
      return dialog.open || dialog.getAttribute("active") === "true";
    }

    /** Open state sits on the `nav-item` ancestor, not on the menu itself. */
    #isMenuOpen(menu) {
      return menu.closest('[expanded="true"]') !== null;
    }

    #deferUntilDialogOpen(dialog) {
      this.#dialog = dialog;
      dialog.addEventListener("drawerOpen", this.#onDialogOpen);
      dialog.addEventListener("modalOpen", this.#onDialogOpen);
    }

    #deferUntilMenuOpen(menu) {
      this.#menu = menu;
      menu.addEventListener("menuOpen", this.#onMenuOpen);
    }

    #onDialogOpen = () => {
      this.payload = this.#parsePayload() ?? this.payload;
      this.#publish();
    };

    #onMenuOpen = () => {
      this.#menu?.removeEventListener("menuOpen", this.#onMenuOpen);
      this.#menu = null;
      this.payload = this.#parsePayload() ?? this.payload;
      this.#publish();
    };

    #publish() {
      WYATT.Analytics.fromElement(this);
    }

    #parsePayload() {
      const payloadScript = this.querySelector("[data-payload]");

      if (!payloadScript?.textContent) {
        return null;
      }

      try {
        return JSON.parse(payloadScript.textContent);
      } catch {
        return null;
      }
    }
  },
);
